import { useRef } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Link, useLangPrefix } from "@/components/LangLink";
import { motion } from "framer-motion";
import {
  ArrowRight, ChevronLeft, ChevronRight, CheckCircle2,
  Globe2, Clock, Lock, ShieldCheck,
  MessageSquare, ClipboardList, CalendarCheck, Sparkles, Bell, Users,
  KeyRound, Search, Building2, Wrench, Paintbrush, Trees, Ruler, Handshake,
  ExternalLink, Home, Eye, Gauge,
} from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import { SERVICES, WHY_PILLARS, PROCESS_STEPS } from "@/data/site";
import { tr } from "@/i18n";

const PILLAR_ICONS = {
  Globe2, Clock, Lock, ShieldCheck, Users, Sparkles,
  KeyRound, Search, Building2, Wrench, Paintbrush, Trees, Ruler, Handshake, Home, Eye,
};
const STEP_ICONS = { MessageSquare, ClipboardList, CalendarCheck, Sparkles, Bell };

const Fleet = ({ s, sectionRef }) => {
  const trackRef = useRef(null);
  const scroll = (dir) => trackRef.current?.scrollBy({ left: dir * (document.documentElement.dir === "rtl" ? -340 : 340), behavior: "smooth" });
  const MetricIcon = s.fleetMetricIcon === "gauge" ? Gauge : Clock;
  return (
    <section ref={sectionRef} className="py-24 lg:py-28 border-b border-white/10">
      <div className="max-w-[110rem] mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between gap-8 mb-12">
          <div>
            <Overline>{s.fleetTitle ? tr(s.fleetTitle) : tr("Jet Charter Options")}</Overline>
            <h2 className="font-serif font-light text-4xl lg:text-5xl mt-5">{s.fleetHeading ? tr(s.fleetHeading) : tr("The right aircraft for every journey.")}</h2>
          </div>
          <div className="hidden sm:flex gap-3 shrink-0">
            <button
              data-testid="fleet-prev"
              onClick={() => scroll(-1)}
              aria-label={tr("Previous aircraft")}
              className="w-12 h-12 border border-gold/60 text-gold flex items-center justify-center hover:bg-gold hover:text-black transition-colors duration-300"
            >
              <ChevronLeft strokeWidth={1.5} size={18} />
            </button>
            <button
              data-testid="fleet-next"
              onClick={() => scroll(1)}
              aria-label={tr("Next aircraft")}
              className="w-12 h-12 border border-gold/60 text-gold flex items-center justify-center hover:bg-gold hover:text-black transition-colors duration-300"
            >
              <ChevronRight strokeWidth={1.5} size={18} />
            </button>
          </div>
        </div>
        <div ref={trackRef} data-testid="fleet-carousel" className="flex gap-5 overflow-x-auto no-scrollbar snap-x pb-2">
          {s.fleet.map((f, i) => (
            <Reveal key={f.name} delay={Math.min(i, 4) * 0.07} className="snap-start shrink-0 w-[280px] sm:w-[320px]">
              <div className="border border-white/10 group h-full flex flex-col bg-[#080808]">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={f.image}
                    alt={f.name}
                    className="w-full h-full object-cover saturate-[0.92] contrast-[1.03] transition-transform duration-[1.3s] ease-out group-hover:scale-105"
                  />
                </div>
                <div className="p-6 flex-1">
                  <p className="text-[0.58rem] uppercase tracking-[0.3em] text-gold">{tr(f.type)}</p>
                  <p className="font-serif text-xl font-light mt-2">{f.name}</p>
                  <div className="flex gap-6 mt-4 text-white/60 text-xs font-light">
                    <span className="flex items-center gap-2">
                      <Users strokeWidth={1.5} size={13} className="text-gold" />
                      {f.pax}
                    </span>
                    <span className="flex items-center gap-2">
                      <MetricIcon strokeWidth={1.5} size={13} className="text-gold" />
                      {tr(f.range)}
                    </span>
                  </div>
                  <p className="text-white/45 text-xs font-light mt-3.5 leading-relaxed">{tr(f.note)}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Related = ({ related }) => (
  <section className="border-t border-white/10 py-20">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <p className="overline-lux mb-10">{tr("Explore Further")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {related.map((r) => (
          <Link key={r.id} to={`/services/${r.id}`} data-testid={`related-${r.id}`} className="group relative aspect-[4/3] overflow-hidden block">
            <img src={r.image} alt={tr(r.name)} className="w-full h-full object-cover transition-transform duration-[1.3s] ease-out group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
            <span className="absolute bottom-5 left-5 font-serif text-xl font-light text-white">{tr(r.name)}</span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const Destinations = ({ s, galleryRef }) => {
  const trackRef = useRef(null);
  const scroll = (dir) => trackRef.current?.scrollBy({ left: dir * (document.documentElement.dir === "rtl" ? -380 : 380), behavior: "smooth" });
  return (
    <section ref={galleryRef} className="py-24 lg:py-28">
      <div className="max-w-[110rem] mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between gap-8 mb-12">
          <div>
            <Overline>{tr("Curated Destinations")}</Overline>
            <h2 className="font-serif font-light text-4xl lg:text-5xl mt-5">{tr("Inspiration for your next escape.")}</h2>
          </div>
          <div className="hidden sm:flex gap-3 shrink-0">
            <button
              data-testid="destinations-prev"
              onClick={() => scroll(-1)}
              aria-label={tr("Previous destinations")}
              className="w-12 h-12 border border-gold/60 text-gold flex items-center justify-center hover:bg-gold hover:text-black transition-colors duration-300"
            >
              <ChevronLeft strokeWidth={1.5} size={18} />
            </button>
            <button
              data-testid="destinations-next"
              onClick={() => scroll(1)}
              aria-label={tr("Next destinations")}
              className="w-12 h-12 border border-gold/60 text-gold flex items-center justify-center hover:bg-gold hover:text-black transition-colors duration-300"
            >
              <ChevronRight strokeWidth={1.5} size={18} />
            </button>
          </div>
        </div>
        <div ref={trackRef} data-testid="destinations-carousel" className="flex gap-5 overflow-x-auto no-scrollbar snap-x pb-2">
          {s.destinations.map((d, i) => (
            <Reveal key={d.name} delay={Math.min(i, 4) * 0.07} className="snap-start shrink-0 w-[280px] sm:w-[340px]">
              <div className="group border border-white/10">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={d.image}
                    alt={tr(d.name)}
                    className="w-full h-full object-cover transition-transform duration-[1.3s] ease-out group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <p className="font-serif text-xl font-light">{tr(d.name)}</p>
                  <p className="text-white/45 text-xs font-light mt-1.5">{tr(d.tagline)}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function ServiceDetail() {
  const { id } = useParams();
  const langPrefix = useLangPrefix();
  const index = SERVICES.findIndex((s) => s.id === id);
  const s = index >= 0 ? SERVICES[index] : null;
  const galleryRef = useRef(null);
  const trackRef = useRef(null);
  const scrollGallery = (dir) => trackRef.current?.scrollBy({ left: dir * 380, behavior: "smooth" });

  if (!s) return <Navigate to={`${langPrefix}/services`} replace />;

  const gallery = s.gallery || [s.image, s.secondary];
  const related = [1, 2, 3].map((n) => SERVICES[(index + n) % SERVICES.length]);

  return (
    <div data-testid={`service-detail-${s.id}`}>
      {/* ——— HERO ——— */}
      <section className="relative min-h-[94vh] flex items-center overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={s.image} alt={tr(s.name)} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/75 to-ink/15" />
        </div>
        <div className="relative max-w-[110rem] mx-auto px-6 lg:px-12 w-full pt-24">
          <MaskedLine delay={0.15}>
            <span className="overline-lux">{tr(s.name)}</span>
          </MaskedLine>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.04] mt-6 max-w-3xl">
            <MaskedLine delay={0.3}>{s.headline ? tr(s.headline) : null}</MaskedLine>
            <MaskedLine delay={0.45}>
              <em className="gold-shimmer italic">{s.headlineItalic ? tr(s.headlineItalic) : null}</em>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.6}>
            <span className="block mt-7 text-white font-medium tracking-wide text-sm">{tr(s.tagline)}</span>
          </MaskedLine>
          <MaskedLine delay={0.7}>
            <span className="block mt-3 max-w-md text-white/60 font-light text-sm leading-relaxed">{tr(s.description)}</span>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to={`/contact?service=${s.id}`}
              data-testid={`service-detail-enquire-${s.id}`}
              className="bg-gold text-black text-xs uppercase tracking-[0.3em] px-9 py-4 hover:bg-gold-bright transition-colors duration-400"
            >{tr("Enquire Now")}</Link>
            {(!s.hideGallery || s.fleet || s.destinations) && (
              <button
                onClick={() => galleryRef.current?.scrollIntoView({ behavior: "smooth" })}
                data-testid="service-detail-view-gallery"
                className="border border-white/40 text-white text-xs uppercase tracking-[0.3em] px-9 py-4 hover:border-gold hover:text-gold transition-colors duration-400"
              >
                {s.heroSecondLabel ? tr(s.heroSecondLabel) : (s.hideGallery && s.fleet ? tr("View Fleet") : tr("View Gallery"))}
              </button>
            )}
            {s.externalUrl && (
              <a
                href={s.externalUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="service-detail-external-link"
                className="border border-gold/60 text-gold text-xs uppercase tracking-[0.3em] px-9 py-4 inline-flex items-center gap-2.5 hover:bg-gold hover:text-black transition-colors duration-400"
              >
                {tr(s.externalLabel)}
                <ExternalLink strokeWidth={1.5} size={13} />
              </a>
            )}
          </motion.div>
        </div>
      </section>

      {/* ——— WHY CHOOSE DELUX ——— */}
      <section className="py-24 lg:py-28 border-b border-white/10">
        <div className="max-w-[110rem] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <Reveal>
              <Overline>{s.pillarsOverline ? tr(s.pillarsOverline) : tr("Why Choose Delux")}</Overline>
              <h2 className="font-serif font-light text-4xl lg:text-5xl leading-[1.08] mt-5">
                {s.pillarsHeading ? tr(s.pillarsHeading) : (
                  <>{tr("More than a service.")}<em className="gold-shimmer italic">{tr("A better way to live.")}</em>
                  </>
                )}
              </h2>
              <p className="text-white/50 font-light leading-relaxed mt-6 text-sm">
                {s.pillarsText ? tr(s.pillarsText) :
                  tr("We deliver a seamless experience with a focus on quality, discretion and efficiency. From short-notice requests to complex arrangements, our team is available 24/7.")}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8 grid grid-cols-2 lg:grid-cols-4 gap-y-12">
            {(s.pillars || WHY_PILLARS).map((p, i) => {
              const Icon = PILLAR_ICONS[p.icon] || Sparkles;
              return (
                <Reveal key={p.title} delay={(i % 4) * 0.08} className={`px-6 lg:px-8 ${i % 4 !== 0 ? "lg:border-l border-white/10" : ""}`}>
                  <Icon strokeWidth={1} size={26} className="text-gold mb-6" />
                  <p className="text-[0.62rem] uppercase tracking-[0.25em] text-white mb-3">{tr(p.title)}</p>
                  <p className="text-xs text-white/45 font-light leading-relaxed">{tr(p.text)}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ——— TAILORED TO YOU ——— */}
      <section className="bg-ivory text-navy">
        <div className="max-w-[110rem] mx-auto grid grid-cols-1 lg:grid-cols-2 items-stretch">
          <div className="relative min-h-[45vh] lg:min-h-[72vh]">
            <img src={s.secondary} alt="" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="px-6 lg:px-20 py-20 lg:py-28 flex flex-col justify-center items-start">
            <Reveal>
              <p className="text-[0.7rem] uppercase tracking-[0.35em] text-navy/50 font-medium">{tr("Tailored To You")}</p>
              <h2 className="font-serif font-light text-4xl lg:text-5xl mt-5">{tr("Every request is unique.")}</h2>
              <p className="text-navy/65 font-light leading-relaxed mt-6 max-w-lg">{tr("Whether for business or leisure, we take care of every detail so you can focus on what matters most.")}</p>
              {s.featuresIntro && (
                <p className="mt-8 font-serif italic text-lg text-navy/60">{tr(s.featuresIntro)}</p>
              )}
              <ul className="mt-7 space-y-4">
                {s.features.map((f) => {
                  const translatedFeature = tr(f);
                  const [lead, ...rest] = translatedFeature.split(" — ");
                  return (
                    <li key={f} className="flex items-start gap-4 text-navy/80 font-light text-[0.95rem]">
                      <CheckCircle2 strokeWidth={1.25} size={17} className="text-gold shrink-0 mt-1" />
                      <span>
                        {rest.length > 0 ? (
                          <>
                            <span className="font-medium text-navy">{lead} — </span>
                            {rest.join(" — ")}
                          </>
                        ) : (
                          translatedFeature
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <Link
                to={`/contact?service=${s.id}`}
                data-testid={`service-detail-enquire-ivory-${s.id}`}
                className="inline-block mt-14 border border-navy/40 text-navy text-xs uppercase tracking-[0.3em] px-9 py-4 hover:bg-navy hover:text-ivory transition-colors duration-400"
              >{tr("Enquire Now")}</Link>
            </Reveal>
          </div>
        </div>
      </section>

      {s.fleet && <Fleet s={s} sectionRef={s.hideGallery && !s.destinations ? galleryRef : undefined} />}

      {s.destinations && <Destinations s={s} galleryRef={galleryRef} />}

      {/* ——— GALLERY CAROUSEL ——— */}
      {!s.hideGallery && !s.destinations && (
      <section ref={galleryRef} className="py-24 lg:py-28">
        <div className="max-w-[110rem] mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between gap-8 mb-12">
            <div>
              <Overline>{tr("The Gallery")}</Overline>
              <h2 className="font-serif font-light text-4xl lg:text-5xl mt-5">{tr("A glimpse of what's possible.")}</h2>
            </div>
            <div className="hidden sm:flex gap-3 shrink-0">
              <button
                data-testid="gallery-prev"
                onClick={() => scrollGallery(-1)}
                aria-label={tr("Previous")}
                className="w-12 h-12 border border-gold/60 text-gold flex items-center justify-center hover:bg-gold hover:text-black transition-colors duration-300"
              >
                <ChevronLeft strokeWidth={1.5} size={18} />
              </button>
              <button
                data-testid="gallery-next"
                onClick={() => scrollGallery(1)}
                aria-label={tr("Next")}
                className="w-12 h-12 border border-gold/60 text-gold flex items-center justify-center hover:bg-gold hover:text-black transition-colors duration-300"
              >
                <ChevronRight strokeWidth={1.5} size={18} />
              </button>
            </div>
          </div>
          <div ref={trackRef} data-testid="service-gallery" className="flex gap-5 overflow-x-auto no-scrollbar snap-x pb-2">
            {gallery.map((g, i) => (
              <Reveal key={g} delay={Math.min(i, 4) * 0.07} className="snap-start shrink-0 w-[290px] sm:w-[360px]">
                <div className="border border-white/10 group">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={g}
                      alt={`${tr(s.name)} — ${i + 1}`}
                      className="w-full h-full object-cover transition-transform duration-[1.3s] ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5 flex items-center justify-between">
                    <span className="font-serif text-xl font-light text-white/85">{tr(s.name)}</span>
                    <span className="text-[0.6rem] uppercase tracking-[0.3em] text-white/30">
                      {String(i + 1).padStart(2, "0")} / {String(gallery.length).padStart(2, "0")}
                    </span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ——— HOW IT WORKS ——— */}
      <section className="bg-ivory text-navy py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <Reveal>
            <p className="text-[0.7rem] uppercase tracking-[0.35em] text-navy/50 font-medium">{tr("How It Works")}</p>
            <h2 className="font-serif font-light text-4xl lg:text-5xl mt-5">{tr("Seamless from start to finish.")}</h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-5 gap-y-12">
            {PROCESS_STEPS.map((st, i) => {
              const Icon = STEP_ICONS[st.icon];
              return (
                <Reveal key={st.num} delay={i * 0.08} className={`px-5 ${i > 0 ? "lg:border-l border-navy/15" : ""}`}>
                  <Icon strokeWidth={1} size={26} className="text-gold mx-auto mb-6" />
                  <p className="text-[0.62rem] uppercase tracking-[0.25em] text-navy mb-2.5">{st.num}. {tr(st.title)}</p>
                  <p className="text-xs text-navy/55 font-light leading-relaxed">{tr(st.text)}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ——— FINAL CTA ——— */}
      <section className="grid grid-cols-1 lg:grid-cols-2 border-t border-white/10">
        <div className="flex flex-col justify-center items-start px-6 lg:px-16 xl:px-24 py-20 lg:py-32">
          <Reveal>
            <Overline>{tr("Ready To Begin?")}</Overline>
            <h2 className="font-serif font-light text-4xl lg:text-5xl leading-[1.08] mt-5">{tr("Your next chapter")}<em className="gold-shimmer italic">{tr("starts here.")}</em>
            </h2>
            <p className="text-white/50 font-light leading-relaxed mt-6 max-w-md text-sm">{tr("Our team is standing by 24/7 to assist with your {{service}} requirements.", { service: tr(s.name).toLowerCase() })}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to={`/contact?service=${s.id}`}
                data-testid={`service-detail-enquire-bottom-${s.id}`}
                className="group bg-gold text-black text-xs uppercase tracking-[0.3em] px-9 py-4 inline-flex items-center gap-3 hover:bg-gold-bright transition-colors duration-400"
              >{tr("Enquire Now")}<ArrowRight strokeWidth={1.5} size={15} className="transition-transform duration-400 group-hover:translate-x-1" />
              </Link>
              {s.externalUrl && (
                <a
                  href={s.externalUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="service-detail-external-bottom"
                  className="border border-white/30 text-white text-xs uppercase tracking-[0.3em] px-9 py-4 inline-flex items-center gap-2.5 hover:border-gold hover:text-gold transition-colors duration-400"
                >
                  {tr(s.externalLabel)}
                  <ExternalLink strokeWidth={1.5} size={13} />
                </a>
              )}
            </div>
          </Reveal>
        </div>
        <div className="relative min-h-[40vh] lg:min-h-[60vh]">
          <img src={s.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/50 to-transparent hidden lg:block" />
        </div>
      </section>

      <Related related={related} />
    </div>
  );
}
